import React, { useEffect, useState } from 'react';
import { employeeAPI } from '../../services/api';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import type { Employee } from '../../types';

const EmployeesPage: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    jobTitle: '',
    phone: '',
    email: '',
    whatsapp: '',
    linkedin: '',
  });
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, [search]);

  const fetchEmployees = async () => {
    try {
      const response = await employeeAPI.getAll({ search });
      setEmployees(response.employees);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingEmployee(null);
    setFormData({
      name: '',
      jobTitle: '',
      phone: '',
      email: '',
      whatsapp: '',
      linkedin: '',
    });
    setPhotoFile(null);
    setShowForm(true);
  };

  const handleEdit = (employee: Employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      jobTitle: employee.jobTitle,
      phone: employee.phone,
      email: employee.email,
      whatsapp: employee.whatsapp || '',
      linkedin: employee.linkedin || '',
    });
    setPhotoFile(null);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) data.append(key, value);
      });
      if (photoFile) {
        data.append('photo', photoFile);
      }

      if (editingEmployee) {
        await employeeAPI.update(editingEmployee._id, data);
      } else {
        await employeeAPI.create(data);
      }

      setShowForm(false);
      fetchEmployees();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error saving employee');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this employee?')) return;

    try {
      await employeeAPI.delete(id);
      fetchEmployees();
    } catch (error) {
      alert('Error deleting employee');
    }
  };

  const handleToggleStatus = async (id: string) => {
    try {
      await employeeAPI.toggleStatus(id);
      fetchEmployees();
    } catch (error) {
      alert('Error updating employee status');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employees</h1>
          <p className="text-gray-600">Manage your team members</p>
        </div>
        <Button onClick={handleAddNew}>+ Add Employee</Button>
      </div>

      {/* Filters */}
      <Card>
        <Input
          placeholder="Search by name, email, or job title..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Card>

      {/* Employee List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {employees.map((employee) => (
          <Card key={employee._id} hover>
            <div className="flex flex-col h-full">
              <div className="flex items-start space-x-4 mb-4">
                {employee.photo ? (
                  <img src={employee.photo} alt={employee.name} className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-2xl">
                    👤
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{employee.name}</h3>
                  <p className="text-sm text-gray-600 truncate">{employee.jobTitle}</p>
                </div>
              </div>

              <div className="space-y-2 mb-4 text-sm">
                <p className="text-gray-600 truncate">📞 {employee.phone}</p>
                <p className="text-gray-600 truncate">✉️ {employee.email}</p>
              </div>

              <div className="mt-auto pt-4 border-t border-gray-200 flex items-center justify-between">
                <span className={`px-2 py-1 text-xs rounded-full ${employee.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {employee.isActive ? 'Active' : 'Inactive'}
                </span>
                <div className="flex space-x-2">
                  <a
                    href={`/card/${employee.uniqueUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-700"
                    title="View Public Profile"
                  >
                    👁️
                  </a>
                  <button
                    onClick={() => handleEdit(employee)}
                    className="text-sunrise-blue-600 hover:text-sunrise-blue-700"
                    title="Edit Employee"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleToggleStatus(employee._id)}
                    className="text-gray-600 hover:text-gray-700"
                    title={employee.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {employee.isActive ? '⏸️' : '▶️'}
                  </button>
                  <button
                    onClick={() => handleDelete(employee._id)}
                    className="text-red-600 hover:text-red-700"
                    title="Delete Employee"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {employees.length === 0 && (
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-500">No employees found</p>
          </div>
        </Card>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold">{editingEmployee ? 'Edit Employee' : 'Add New Employee'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-700 text-2xl">
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Full Name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                <Input
                  label="Job Title"
                  required
                  value={formData.jobTitle}
                  onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Phone Number"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
                <Input
                  label="Email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="WhatsApp (optional)"
                  type="tel"
                  value={formData.whatsapp}
                  onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                />
                <Input
                  label="LinkedIn URL (optional)"
                  type="url"
                  value={formData.linkedin}
                  onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Photo
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <Button type="submit" isLoading={submitting}>
                  {editingEmployee ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeesPage;
